import { getToday, addDays, parseDate, formatToStandardDate, addYears, } from '@buddy-technology/buddy_helpers';
import US from '../models/states';
const defaultOptions = {
    timezone: undefined,
};
/**
 * Recurses through an ION and fills in any values that reference a variable.
 * Anything string prefixed with: '$variables.' will be substituted with a reference to the ION variable
 * $TODAY substitutes today's date in MM/DD/YYYY format.
 * $TOMORROW substitutes tomorrow's date in MM/DD/YYYY format.
 * Strings prefixed with '$US_' will sub in the appropriate STATE model
 * @see {@link ../docs/substituteVars.md}
 * @param {object} _ion - The raw ion object.
 * @param {substituteVarsOptions} _options - an optional configuration object
 * @returns {object} Ion object with injected variables.
 * @memberof module:ionHelpers
 */
const substituteVars = (_ion, _options = defaultOptions) => {
    if (!_ion) {
        throw new Error('An Ion is required. None was passed.');
    }
    const { timezone, } = _options;
    const today = getToday(timezone);
    const todaysParsedDate = parseDate(today);
    if (todaysParsedDate === null) {
        throw new Error('Unable to parse the contextual $TODAY value.');
    }
    const tomorrowsParsedDate = addDays(todaysParsedDate, 1);
    const tomorrow = formatToStandardDate(tomorrowsParsedDate);
    const yearFromToday = formatToStandardDate(addYears(todaysParsedDate, 1));
    const yearFromTomorrow = formatToStandardDate(addYears(tomorrowsParsedDate, 1));
    const { variables, _calls } = _ion;
    if (!variables || !Object.keys(variables).length) {
        return _ion;
    }
    const retrieveReservedValue = (value) => {
        if (value === '$TODAY') {
            return today;
        }
        else if (value === '$TOMORROW') {
            return tomorrow;
        }
        else if (value === '$YEAR_FROM_TODAY') {
            return yearFromToday;
        }
        else if (value === '$YEAR_FROM_TOMORROW') {
            return yearFromTomorrow;
        }
        else if (typeof value === 'string' && value.includes('$US_')) {
            const path = value.substring(4);
            return US[path];
        }
        return value;
    };
    const deepMap = (entity) => {
        // const returnVal: Record<string, any> = Array.isArray(entity) ? [] : {};
        const returnVal = Array.isArray(entity) ? [] : {};
        // length of $variables. (<- period included)
        const variablesSubStringLength = 11;
        // length of $_calls. (<- period included)
        const callsSubstringLength = 8;
        Object.entries(entity).forEach(([key, value]) => {
            // recurse through objects, unless they are a _calculate or expression (reserved keys to hold mingo expressions)
            if (['_calculate', 'expression', 'calculateData', 'calculateForms'].includes(key)) {
                returnVal[key] = value;
            }
            else if (value && typeof value === 'object') {
                returnVal[key] = deepMap(value);
            }
            else if (typeof value === 'string' && value.includes('$variables.')) {
                const variableValue = variables[value.substring(variablesSubStringLength)];
                returnVal[key] = retrieveReservedValue(variableValue);
            }
            else if (typeof value === 'string' && value.includes('$_calls.')) {
                if (_calls) {
                    const callObj = _calls[value.substring(callsSubstringLength)];
                    returnVal[key] = callObj._call;
                }
            }
            else {
                returnVal[key] = retrieveReservedValue(value);
            }
        });
        return returnVal;
    };
    return deepMap(_ion);
};
export default substituteVars;
